// The arithmetic under the season charts. P6-329c (GN-2c).
//
// Pure functions, no DOM and no React: the design's own helpers (`niceTicks`, `monthsOf`, `spark`, `trajDom`,
// `miniTraj`), ported, and tested as numbers. The components draw what these return and nothing more, so every
// claim a chart makes (where the zero line is, which game is the season high, which month a label sits under) is
// asserted here as a value and not looked for in a screenshot.
//
// ═══════════════════════════════════════════════════════════════════════════
// A MISSING VALUE IS A GAP, NEVER A ZERO
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's `spark` was written over samples whose `last10` were all numbers. The REAL leaders files are not: a
// free-throw leader who did not shoot one in a game has `null` for that game (`[1, 1, 1, null, null, 1, ...]`), and
// `Math.min(null, 3)` is `0`. A sparkline built the design's way draws a free-throw percentage of nothing at the
// bottom of the chart for a game he did not attempt one, and pulls the range down with it. So a value that is not a
// finite number is left out: the line is broken there, and the range is the range of what was measured.
//
// A RUN OF ONE POINT IS DRAWN AS A POINT. A path that is only `M x y` draws nothing, so an isolated game between
// two missing ones would vanish; it is written as `M x y L x y`, which a round line cap draws as a dot.

import { isNumber } from './historyFormat.js'

/** Round to two decimals for a path, as text. */
const two = (value) => value.toFixed(2)

/**
 * Tick values that land on round numbers and cover `lo` to `hi`, about `count` of them.
 *
 * Returns the covered range (`lo`, `hi`, which are the first and last tick, or one step past it when the data is
 * flat) and the ticks. The step is 1, 2, 2.5, 5 or 10 times a power of ten.
 */
export function niceTicks(lo, hi, count) {
  const span = hi - lo || 1
  const raw = span / count
  const power = Math.pow(10, Math.floor(Math.log10(raw)))
  const mantissa = raw / power
  const step = (mantissa <= 1 ? 1 : mantissa <= 2 ? 2 : mantissa <= 2.5 ? 2.5 : mantissa <= 5 ? 5 : 10) * power
  const first = Math.floor(lo / step + 1e-9) * step
  const last = Math.ceil(hi / step - 1e-9) * step
  const ticks = []
  for (let value = first; value <= last + step / 1e6; value += step) ticks.push(Number(value.toFixed(6)))
  return { lo: first, hi: last === first ? first + step : last, ticks }
}

/**
 * Where month names go under a chart: the first game of each month, at least 10% after the last label and not past
 * 92%, so a label never runs off the right edge or onto its neighbour. `dates` are `YYYY-MM-DD`, `xOf` maps a game's
 * index to its place on the axis in percent, and `name` turns a date into the label.
 */
export function monthLabels(dates, xOf, name) {
  const out = []
  let lastMonth = null
  let lastX = -99
  dates.forEach((date, index) => {
    const month = String(date).slice(0, 7)
    if (month === lastMonth) return
    lastMonth = month
    const x = xOf(index)
    if (x - lastX >= 10 && x <= 92) {
      out.push({ left: x, label: name(date) })
      lastX = x
    }
  })
  return out
}

/** Moves of a path through points, restarting wherever a point is missing; a run of one is drawn as a point. */
function pathThrough(points) {
  let d = ''
  let run = []
  const flush = () => {
    if (run.length === 1) d += `M${two(run[0][0])} ${two(run[0][1])} L${two(run[0][0])} ${two(run[0][1])} `
    else if (run.length > 1) d += run.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${two(x)} ${two(y)}`).join(' ') + ' '
    run = []
  }
  for (const point of points) {
    if (point === null) flush()
    else run.push(point)
  }
  flush()
  return d.trim()
}

/**
 * The line under a leader: his last ten games on a 100-wide box `height` tall, with a dotted line at his season mark.
 *
 * `values` may hold nulls (a game with nothing to measure), and `mark` may be `null` (no season mark to draw).
 * Returns the path, the path of the mark line, the end dot's place in percent (`null` when nothing was measured),
 * and the box. `empty` is true when there is nothing to draw at all.
 */
export function sparkline(values, mark, height = 30) {
  const measured = (Array.isArray(values) ? values : []).filter(isNumber)
  const markValue = isNumber(mark) ? mark : null
  const box = `0 0 100 ${height}`
  if (measured.length === 0) return { d: '', base: '', dot: null, box, empty: true }
  const all = markValue === null ? measured : [...measured, markValue]
  const lo = Math.min(...all)
  const hi = Math.max(...all)
  const range = hi - lo || 1
  const y = (value) => 3 + ((hi - value) / range) * (height - 6)
  const x = (index) => (values.length > 1 ? (index / (values.length - 1)) * 100 : 50)
  const points = values.map((value, index) => (isNumber(value) ? [x(index), y(value)] : null))
  let last = values.length - 1
  while (last >= 0 && !isNumber(values[last])) last -= 1
  return {
    d: pathThrough(points),
    base: markValue === null ? '' : `M0 ${two(y(markValue))} L100 ${two(y(markValue))}`,
    dot: { left: Number(x(last).toFixed(1)), top: Number(((y(values[last]) / height) * 100).toFixed(1)) },
    box,
    empty: false,
  }
}

/**
 * The widest a team's record ever got from level, in games, across every team in one season: the scale every team's
 * line shares, so a line in one row can be compared with a line in another. At least 1.
 */
export function trajectoryDomain(standings) {
  let widest = 1
  for (const trajectory of Object.values(standings?.trajectories ?? {})) {
    const columns = trajectory?.columns ?? []
    const wins = columns.indexOf('w')
    const losses = columns.indexOf('l')
    if (wins < 0 || losses < 0) continue
    for (const row of trajectory.rows ?? []) widest = Math.max(widest, Math.abs(row[wins] - row[losses]))
  }
  return widest
}

/** A trajectory's `[date, w, l]` rows as `{ dates, over }`: the dates, and games over .500 after each. */
export function overSeries(trajectory) {
  const columns = trajectory?.columns ?? []
  const date = columns.indexOf('date')
  const wins = columns.indexOf('w')
  const losses = columns.indexOf('l')
  const rows = Array.isArray(trajectory?.rows) ? trajectory.rows : []
  return {
    dates: rows.map((row) => row[date]),
    wins: rows.map((row) => row[wins]),
    losses: rows.map((row) => row[losses]),
    over: rows.map((row) => row[wins] - row[losses]),
  }
}

/**
 * One team's season as a small line of games over .500 on the shared scale: `height` tall, level through the middle.
 * The dotted line is .500. Returns the path, the zero line, the end dot and the last mark.
 */
export function miniTrajectory(trajectory, domain, height) {
  const { over } = overSeries(trajectory)
  const count = over.length
  const box = `0 0 100 ${height}`
  if (count === 0) return { d: '', zero: `M0 ${height / 2} L100 ${height / 2}`, box, dot: null, end: 0, empty: true }
  const y = (value) => 2 + ((domain - value) / (2 * domain)) * (height - 4)
  const x = (index) => (count > 1 ? (index / (count - 1)) * 100 : 50)
  return {
    d: pathThrough(over.map((value, index) => [x(index), y(value)])),
    zero: `M0 ${height / 2} L100 ${height / 2}`,
    box,
    dot: { left: Number(x(count - 1).toFixed(1)), top: Number(((y(over[count - 1]) / height) * 100).toFixed(1)) },
    end: over[count - 1],
    empty: false,
  }
}

/** `index` held inside `0 .. count - 1`. */
export const clampIndex = (index, count) => Math.max(0, Math.min(count - 1, index))

/**
 * Which of `count` equal columns a pointer is over, from its x and the chart's box, or `null` when the box has no
 * width to measure (a layout that has not happened yet, or jsdom).
 */
export function columnAt(clientX, box, count) {
  if (!box || !(box.width > 0) || count < 1) return null
  return clampIndex(Math.floor(((clientX - box.left) / box.width) * count), count)
}

const KEY_STEP = Object.freeze({ ArrowLeft: -1, ArrowRight: 1, ArrowDown: -1, ArrowUp: 1 })

/**
 * Where a key moves the selection: an arrow steps one game, Home goes to the first and End to the last. `null` for a
 * key that does nothing here, so the caller leaves the browser's own handling of it alone.
 */
export function keyTarget(key, current, count) {
  if (typeof key === 'string' && Object.hasOwn(KEY_STEP, key)) return clampIndex(current + KEY_STEP[key], count)
  if (key === 'Home') return 0
  if (key === 'End') return count - 1
  return null
}
